import { logger } from '../utils/logger';

interface SessionFingerprint {
  userId: string;
  email: string;
  role: string;
  loginTimestamp: number;
  version: string;
}

interface SessionIntegrityCheck {
  isValid: boolean;
  reason?: string;
  severity?: 'warning' | 'critical';
}

class SessionIntegrityService {
  private readonly STORAGE_KEY = 'crm_session_fingerprint';
  private currentFingerprint: SessionFingerprint | null = null;

  setSessionFingerprint(userId: string, email: string, role: string, version: string): void {
    const fingerprint: SessionFingerprint = {
      userId,
      email,
      role,
      loginTimestamp: Date.now(),
      version
    };

    this.currentFingerprint = fingerprint;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(fingerprint));
      logger.debug('Session fingerprint establecido', { userId, email, role, version });
    } catch (error) {
      logger.error('Error guardando fingerprint', error);
    }
  }

  getSessionFingerprint(): SessionFingerprint | null {
    if (this.currentFingerprint) {
      return this.currentFingerprint;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.currentFingerprint = JSON.parse(stored);
        return this.currentFingerprint;
      }
    } catch (error) {
      logger.error('Error leyendo fingerprint', error);
    }

    return null;
  }

  validateSessionIntegrity(
    userId: string,
    email: string,
    role: string,
    context: 'initial_login' | 'session_reload' | 'token_refresh' | 'auth_event'
  ): SessionIntegrityCheck {
    const fingerprint = this.getSessionFingerprint();

    if (!fingerprint) {
      // Missing fingerprint is legitimate in many cases: fresh login, cleared
      // localStorage, incognito, other device, or first load after deploy.
      // Treat as valid — the caller will set a fresh fingerprint after this.
      logger.debug(`No hay fingerprint almacenado durante ${context} — tratando como reload legítimo`);
      return { isValid: true };
    }

    if (fingerprint.userId !== userId) {
      console.error('🚨 CRÍTICO: Cambio de user_id detectado!', {
        expected: fingerprint.userId,
        received: userId,
        context
      });

      this.logSecurityEvent('user_id_mismatch', {
        expectedUserId: fingerprint.userId,
        receivedUserId: userId,
        expectedEmail: fingerprint.email,
        receivedEmail: email,
        context
      });

      return {
        isValid: false,
        reason: `User ID mismatch: expected ${fingerprint.userId}, got ${userId}`,
        severity: 'critical'
      };
    }

    if (fingerprint.email !== email) {
      console.error('🚨 CRÍTICO: Cambio de email detectado!', {
        expected: fingerprint.email,
        received: email,
        context
      });

      this.logSecurityEvent('email_mismatch', {
        expectedEmail: fingerprint.email,
        receivedEmail: email,
        userId,
        context
      });

      return {
        isValid: false,
        reason: `Email mismatch: expected ${fingerprint.email}, got ${email}`,
        severity: 'critical'
      };
    }

    if (fingerprint.role !== role && context !== 'initial_login') {
      console.warn('⚠️ Cambio de rol detectado:', {
        expected: fingerprint.role,
        received: role,
        context
      });

      this.logSecurityEvent('role_change_detected', {
        expectedRole: fingerprint.role,
        receivedRole: role,
        userId,
        email,
        context
      });

      // Role changes are legitimate (admin might have changed user's role in DB)
      // Update fingerprint with new role instead of invalidating session
      logger.info('Actualizando fingerprint con nuevo rol', role);
      this.currentFingerprint = {
        ...fingerprint,
        role
      };

      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.currentFingerprint));
      } catch (error) {
        logger.error('Error actualizando fingerprint', error);
      }

      // Return warning but allow session to continue
      return {
        isValid: true,
        reason: `Role changed from ${fingerprint.role} to ${role} during ${context}`,
        severity: 'warning'
      };
    }

    return { isValid: true };
  }

  clearSessionFingerprint(): void {
    this.currentFingerprint = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('🔓 Session fingerprint limpiado');
    } catch (error) {
      logger.error('Error limpiando fingerprint', error);
    }
  }

  private logSecurityEvent(eventType: string, metadata: any): void {
    const event = {
      type: 'security_event',
      eventType,
      timestamp: new Date().toISOString(),
      metadata,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.error('🚨 EVENTO DE SEGURIDAD:', event);

    window.dispatchEvent(new CustomEvent('session-integrity-violation', {
      detail: event
    }));
  }

  isSessionExpired(maxAgeHours: number = 24): boolean {
    const fingerprint = this.getSessionFingerprint();
    if (!fingerprint) return true;

    const ageMs = Date.now() - fingerprint.loginTimestamp;
    const ageHours = ageMs / (1000 * 60 * 60);

    return ageHours > maxAgeHours;
  }

  getSessionAge(): number | null {
    const fingerprint = this.getSessionFingerprint();
    if (!fingerprint) return null;

    const ageMs = Date.now() - fingerprint.loginTimestamp;
    return Math.floor(ageMs / 1000);
  }
}

export const sessionIntegrityService = new SessionIntegrityService();
export default sessionIntegrityService;
